/**
 * Other’s Audio: Translate What the Other Side Says (feature guide)
 *
 * 02-mode-devices.png and 03-translation-i-hear.png were taken from the Sokuji
 * v0.44.0 desktop app; 01-others-audio.png from v0.41.1.
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './tutorials/TutorialTemplate';

const en: TutorialData = {
  "pageTitle": "Other’s Audio: Translate What the Other Side Says",
  "backLink": "Back to Documentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Overview",
    "content": "In Other and Both modes, Sokuji needs the other side’s audio: the voices of a meeting, a video or a live stream. The desktop app takes it from a specific application or from everything your computer plays; the browser extension takes it from the current tab. The original sound keeps playing as usual, and the translation is shown as text, or also read aloud to you with Translation I hear."
  },
  "steps": [
    {
      "title": "Choose the source (desktop app)",
      "content": "In Settings, under <strong>Other’s audio</strong>, pick the application that plays the sound, for example <strong>Microsoft Teams</strong>, or <strong>System Audio (All Applications)</strong> for everything your computer plays. Picking the application keeps notification sounds and music from other programs out of the translation.",
      "screenshot": "01-others-audio.png",
      "tip": "An application often appears in the list only once it is playing sound. Join the call or start the video first, then click the refresh icon."
    },
    {
      "title": "Change it from the main window",
      "content": "Click the selected mode at the bottom left. In Other mode it shows <strong>Other’s audio</strong>, which you can switch on or off or point at another source, and <strong>Translation I hear</strong>.",
      "screenshot": "02-mode-devices.png"
    },
    {
      "title": "Hear the translation",
      "content": "To have the other side’s translation read aloud to you as well, switch on <strong>Translation I hear</strong> in the <strong>Speech</strong> block and pick the device and channel it plays on. In the desktop app this needs one application as the source: with System Audio (All Applications) the row is greyed, because the translation would be captured and translated again. It cannot change while a session runs. See <a href=\"/docs/features/text-only\">Spoken or Text Only</a>.",
      "screenshot": "03-translation-i-hear.png"
    },
    {
      "title": "Browser extension",
      "content": "In the extension, Other’s audio is the tab you are in, for example your meeting. Switch it on; the tab’s own sound keeps playing on the <strong>Default playback device</strong> chosen in the Speech block when the session starts."
    },
    {
      "title": "macOS permission",
      "content": "On macOS, capturing all system audio needs the <strong>System Audio Recording Only</strong> permission. If no sound arrives, Sokuji says so and offers <strong>Open System Settings</strong>. Picking a specific application instead also works."
    }
  ],
  "tips": {
    "title": "Tips",
    "items": [
      "On Linux, wear headphones in Both mode, so that your microphone does not pick up the other side from your speakers. On Windows and macOS, echo cancellation removes what your computer plays from your microphone.",
      "Other’s audio is translated as text. It is read aloud to you only when Translation I hear is on."
    ]
  },
  "faq": {
    "title": "Frequently Asked Questions",
    "items": [
      {
        "question": "The other side’s words are not translated. What should I check?",
        "answer": "That Sokuji is in Other or Both mode, that Other’s audio is set to the right application or to System Audio (All Applications), and that the call is actually playing sound."
      },
      {
        "question": "Why is Translation I hear greyed?",
        "answer": "In the desktop app, it needs one application as Other’s audio source: pick the application instead of System Audio (All Applications). It is also greyed in Me mode, and fixed while a session runs."
      },
      {
        "question": "Can I change the source during a session?",
        "answer": "Yes, in the desktop app. If the switch fails, Sokuji keeps the previous source; stop and start the session to change it."
      }
    ]
  },
  "troubleshooting": {
    "title": "Troubleshooting",
    "content": "Still stuck? Send us feedback from the account menu, or write to <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. You can also ask in <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const zh: TutorialData = {
  "pageTitle": "对方音频：翻译对方说的话",
  "backLink": "返回文档",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "概述",
    "content": "在对方和两者模式下，Sokuji 需要对方的音频：会议、视频或直播中的声音。桌面应用可以从某个指定应用或电脑播放的所有声音中获取；浏览器扩展则从当前标签页获取。原声会照常播放，翻译以文字显示，开启“我听到的翻译”时还会朗读给你听。"
  },
  "steps": [
    {
      "title": "选择来源（桌面应用）",
      "content": "在设置的<strong>对方音频</strong>下，选择播放声音的应用，例如 <strong>Microsoft Teams</strong>；或者选择 <strong>System Audio (All Applications)</strong> 以获取电脑播放的所有声音。选择具体应用可以避免其他程序的通知音和音乐被翻译。",
      "screenshot": "01-others-audio.png",
      "tip": "应用通常要在播放声音后才会出现在列表中。请先加入通话或开始播放视频，然后点击刷新图标。"
    },
    {
      "title": "在主窗口中更改",
      "content": "点击左下角当前选中的模式。在“对方”模式下，它会显示<strong>对方音频</strong>（可以开启或关闭，或选择其他来源）和<strong>我听到的翻译</strong>。",
      "screenshot": "02-mode-devices.png"
    },
    {
      "title": "收听翻译",
      "content": "如果还想听到对方的翻译被朗读出来，请在<strong>语音</strong>区块中开启<strong>我听到的翻译</strong>，并选择播放它的设备和声道。在桌面应用中，这需要把某一个应用设为来源：选择 System Audio (All Applications) 时这一行是灰色的，因为翻译会被再次捕获并翻译。会话运行期间无法更改。请参阅<a href=\"/docs/features/text-only\">朗读或仅文字</a>。",
      "screenshot": "03-translation-i-hear.png"
    },
    {
      "title": "浏览器扩展",
      "content": "在扩展中，对方音频就是你当前所在的标签页，例如你的会议。将其开启即可；标签页本身的声音会继续在会话开始时语音区块中选定的<strong>默认播放设备</strong>上播放。"
    },
    {
      "title": "macOS 权限",
      "content": "在 macOS 上，捕获所有系统音频需要<strong>仅系统录音</strong>权限。如果没有收到任何声音，Sokuji 会提示你，并提供<strong>打开系统设置</strong>。改为选择某个具体应用也可以。"
    }
  ],
  "tips": {
    "title": "提示",
    "items": [
      "在 Linux 上，使用“两者”模式时请戴耳机，以免你的麦克风从扬声器中收录到对方的声音。在 Windows 和 macOS 上，回声消除会从你的麦克风中去除电脑播放的声音。",
      "对方音频会以文字形式翻译。只有开启“我听到的翻译”时才会朗读给你听。"
    ]
  },
  "faq": {
    "title": "常见问题",
    "items": [
      {
        "question": "对方的话没有被翻译，应该检查什么？",
        "answer": "检查 Sokuji 是否处于对方或两者模式，对方音频是否设为正确的应用或 System Audio (All Applications)，以及通话是否确实在播放声音。"
      },
      {
        "question": "为什么“我听到的翻译”是灰色的？",
        "answer": "在桌面应用中，它需要把某一个应用设为对方音频的来源：请选择具体的应用，而不是 System Audio (All Applications)。在“我”模式下它也是灰色的，会话运行期间则固定不变。"
      },
      {
        "question": "会话期间可以更改来源吗？",
        "answer": "可以，在桌面应用中。如果切换失败，Sokuji 会保留之前的来源；停止并重新开始会话即可更改。"
      }
    ]
  },
  "troubleshooting": {
    "title": "故障排除",
    "content": "仍然无法解决？可以从账号菜单向我们发送反馈，或发送邮件至 <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>。你也可以在 <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> 中提问。"
  }
};

const ja: TutorialData = {
  "pageTitle": "相手の音声：相手の発言を翻訳する",
  "backLink": "ドキュメントに戻る",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "概要",
    "content": "相手モードと両方モードでは、Sokuji に相手の音声（会議、動画、ライブ配信の声）が必要です。デスクトップアプリは特定のアプリか、パソコンで再生されるすべての音から取得し、ブラウザー拡張機能は現在のタブから取得します。元の音はいつもどおり再生され、翻訳はテキストで表示されます。「自分に聞こえる翻訳」をオンにすれば、あなたに向けて読み上げもされます。"
  },
  "steps": [
    {
      "title": "ソースを選ぶ（デスクトップアプリ）",
      "content": "設定の<strong>相手の音声</strong>で、音を再生しているアプリ（例：<strong>Microsoft Teams</strong>）を選ぶか、パソコンで再生されるすべての音を対象にする場合は <strong>System Audio (All Applications)</strong> を選びます。アプリを選ぶと、ほかのプログラムの通知音や音楽が翻訳に入りません。",
      "screenshot": "01-others-audio.png",
      "tip": "アプリは音を再生して初めて一覧に表示されることがよくあります。先に通話に参加するか動画を再生してから、更新アイコンをクリックしてください。"
    },
    {
      "title": "メイン画面から変更する",
      "content": "左下の選択中のモードをクリックします。相手モードでは<strong>相手の音声</strong>（オン・オフの切り替えや別のソースの選択ができます）と<strong>自分に聞こえる翻訳</strong>が表示されます。",
      "screenshot": "02-mode-devices.png"
    },
    {
      "title": "翻訳を聞く",
      "content": "相手の翻訳を読み上げでも聞くには、<strong>音声</strong>ブロックで<strong>自分に聞こえる翻訳</strong>をオンにし、再生するデバイスとチャンネルを選びます。デスクトップアプリでは、ソースに 1 つのアプリを選ぶ必要があります。System Audio (All Applications) を選んでいると、翻訳がもう一度キャプチャされて再翻訳されてしまうため、この行はグレー表示になります。セッションの実行中は変更できません。<a href=\"/docs/features/text-only\">読み上げまたはテキストのみ</a>もご覧ください。",
      "screenshot": "03-translation-i-hear.png"
    },
    {
      "title": "ブラウザー拡張機能",
      "content": "拡張機能では、相手の音声は今開いているタブ（例：会議のタブ）です。オンにしてください。タブ自体の音は、セッション開始時に「音声」ブロックで選んだ<strong>既定の再生デバイス</strong>でそのまま再生されます。"
    },
    {
      "title": "macOS の権限",
      "content": "macOS でシステム全体の音声を取得するには、<strong>システムオーディオ録音のみ</strong>の権限が必要です。音声が届かない場合は Sokuji がそのことを知らせ、<strong>システム設定を開く</strong>を表示します。代わりに特定のアプリを選んでも動作します。"
    }
  ],
  "tips": {
    "title": "ヒント",
    "items": [
      "Linux では、両方モードのときはヘッドホンを着けてください。スピーカーから出る相手の声をマイクが拾わないようにするためです。Windows と macOS では、エコーキャンセルがパソコンで再生される音をマイクから取り除きます。",
      "相手の音声はテキストで翻訳されます。読み上げられるのは「自分に聞こえる翻訳」がオンのときだけです。"
    ]
  },
  "faq": {
    "title": "よくある質問",
    "items": [
      {
        "question": "相手の発言が翻訳されません。何を確認すればよいですか？",
        "answer": "Sokuji が相手モードか両方モードになっているか、相手の音声が正しいアプリか System Audio (All Applications) に設定されているか、通話で実際に音が再生されているかを確認してください。"
      },
      {
        "question": "「自分に聞こえる翻訳」がグレーになっているのはなぜですか？",
        "answer": "デスクトップアプリでは、相手の音声のソースに 1 つのアプリを選ぶ必要があります。System Audio (All Applications) ではなくアプリを選んでください。自分モードでもグレー表示になり、セッションの実行中は固定されます。"
      },
      {
        "question": "セッション中にソースを変更できますか？",
        "answer": "はい、デスクトップアプリでは可能です。切り替えに失敗した場合、Sokuji は以前のソースを使い続けます。変更するにはセッションを停止して開始し直してください。"
      }
    ]
  },
  "troubleshooting": {
    "title": "トラブルシューティング",
    "content": "それでも解決しない場合は、アカウントメニューからフィードバックを送るか、<a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a> までご連絡ください。<a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> で質問することもできます。"
  }
};

const ko: TutorialData = {
  "pageTitle": "상대방 오디오: 상대방의 말 번역하기",
  "backLink": "문서로 돌아가기",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "개요",
    "content": "상대방 모드와 모두 모드에서 Sokuji에는 상대방의 오디오, 즉 회의·동영상·라이브 방송의 음성이 필요합니다. 데스크톱 앱은 특정 앱이나 컴퓨터에서 재생되는 모든 소리에서 가져오고, 브라우저 확장 프로그램은 현재 탭에서 가져옵니다. 원래 소리는 평소처럼 재생되며, 번역은 텍스트로 표시되고 “내가 듣는 번역”을 켜면 나에게 음성으로도 읽어 줍니다."
  },
  "steps": [
    {
      "title": "소스 선택(데스크톱 앱)",
      "content": "설정의 <strong>상대방 오디오</strong>에서 소리를 재생하는 앱(예: <strong>Microsoft Teams</strong>)을 고르거나, 컴퓨터에서 재생되는 모든 소리를 원하면 <strong>System Audio (All Applications)</strong>를 고르세요. 앱을 고르면 다른 프로그램의 알림음과 음악이 번역에 섞이지 않습니다.",
      "screenshot": "01-others-audio.png",
      "tip": "앱은 소리를 재생해야 목록에 나타나는 경우가 많습니다. 먼저 통화에 참여하거나 동영상을 재생한 다음 새로고침 아이콘을 클릭하세요."
    },
    {
      "title": "메인 창에서 변경",
      "content": "왼쪽 아래의 선택된 모드를 클릭하세요. 상대방 모드에서는 <strong>상대방 오디오</strong>(켜거나 끄거나 다른 소스를 고를 수 있음)와 <strong>내가 듣는 번역</strong>이 표시됩니다.",
      "screenshot": "02-mode-devices.png"
    },
    {
      "title": "번역 듣기",
      "content": "상대방의 번역을 음성으로도 들으려면 <strong>음성</strong> 블록에서 <strong>내가 듣는 번역</strong>을 켜고 재생할 장치와 채널을 고르세요. 데스크톱 앱에서는 소스로 앱 하나를 골라야 합니다. System Audio (All Applications)를 고르면 번역이 다시 캡처되어 또 번역되므로 이 행이 회색으로 표시됩니다. 세션이 실행되는 동안에는 바꿀 수 없습니다. <a href=\"/docs/features/text-only\">음성 또는 텍스트만</a>을 참고하세요.",
      "screenshot": "03-translation-i-hear.png"
    },
    {
      "title": "브라우저 확장 프로그램",
      "content": "확장 프로그램에서는 상대방 오디오가 지금 보고 있는 탭(예: 회의 탭)입니다. 켜기만 하면 됩니다. 탭 자체의 소리는 세션을 시작할 때 “음성” 블록에서 선택한 <strong>기본 재생 장치</strong>에서 계속 재생됩니다."
    },
    {
      "title": "macOS 권한",
      "content": "macOS에서 시스템 전체 오디오를 캡처하려면 <strong>시스템 오디오 녹음만</strong> 권한이 필요합니다. 소리가 들어오지 않으면 Sokuji가 이를 알리고 <strong>시스템 설정 열기</strong>를 제공합니다. 대신 특정 앱을 골라도 됩니다."
    }
  ],
  "tips": {
    "title": "팁",
    "items": [
      "Linux에서는 모두 모드일 때 스피커에서 나오는 상대방의 소리가 마이크에 들어가지 않도록 헤드폰을 착용하세요. Windows와 macOS에서는 에코 제거가 컴퓨터에서 재생되는 소리를 마이크에서 제거합니다.",
      "상대방 오디오는 텍스트로 번역됩니다. “내가 듣는 번역”이 켜져 있을 때만 나에게 음성으로 읽어 줍니다."
    ]
  },
  "faq": {
    "title": "자주 묻는 질문",
    "items": [
      {
        "question": "상대방의 말이 번역되지 않습니다. 무엇을 확인해야 하나요?",
        "answer": "Sokuji가 상대방 모드나 모두 모드인지, 상대방 오디오가 올바른 앱이나 System Audio (All Applications)로 설정되어 있는지, 통화에서 실제로 소리가 재생되고 있는지 확인하세요."
      },
      {
        "question": "“내가 듣는 번역”이 회색으로 표시되는 이유는 무엇인가요?",
        "answer": "데스크톱 앱에서는 상대방 오디오 소스로 앱 하나를 골라야 합니다. System Audio (All Applications) 대신 앱을 고르세요. 나 모드에서도 회색으로 표시되며, 세션이 실행되는 동안에는 고정됩니다."
      },
      {
        "question": "세션 중에 소스를 바꿀 수 있나요?",
        "answer": "네, 데스크톱 앱에서는 가능합니다. 전환에 실패하면 Sokuji는 이전 소스를 유지합니다. 바꾸려면 세션을 중지했다가 다시 시작하세요."
      }
    ]
  },
  "troubleshooting": {
    "title": "문제 해결",
    "content": "그래도 해결되지 않나요? 계정 메뉴에서 피드백을 보내거나 <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>로 이메일을 보내 주세요. <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>에서 질문할 수도 있습니다."
  }
};

const es: TutorialData = {
  "pageTitle": "Audio del otro: traduce lo que dice la otra parte",
  "backLink": "Volver a la documentación",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Descripción general",
    "content": "En los modos Otro y Ambos, Sokuji necesita el audio de la otra parte: las voces de una reunión, un vídeo o un directo. La aplicación de escritorio lo toma de una aplicación concreta o de todo lo que suena en tu ordenador; la extensión de navegador lo toma de la pestaña actual. El sonido original se sigue reproduciendo como siempre, y la traducción se muestra como texto o, con Traducción que yo oigo, también se te lee en voz alta."
  },
  "steps": [
    {
      "title": "Elige la fuente (aplicación de escritorio)",
      "content": "En Configuración, en <strong>Audio del otro</strong>, elige la aplicación que reproduce el sonido, por ejemplo <strong>Microsoft Teams</strong>, o <strong>System Audio (All Applications)</strong> para todo lo que suena en tu ordenador. Si eliges la aplicación, los sonidos de notificación y la música de otros programas quedan fuera de la traducción.",
      "screenshot": "01-others-audio.png",
      "tip": "A menudo una aplicación solo aparece en la lista cuando está reproduciendo sonido. Únete primero a la llamada o inicia el vídeo y luego haz clic en el icono de actualizar."
    },
    {
      "title": "Cámbiala desde la ventana principal",
      "content": "Haz clic en el modo seleccionado, abajo a la izquierda. En el modo Otro muestra <strong>Audio del otro</strong>, que puedes activar, desactivar o cambiar a otra fuente, y <strong>Traducción que yo oigo</strong>.",
      "screenshot": "02-mode-devices.png"
    },
    {
      "title": "Escucha la traducción",
      "content": "Para que también se te lea en voz alta la traducción de la otra parte, activa <strong>Traducción que yo oigo</strong> en el bloque <strong>Voz</strong> y elige el dispositivo y el canal en los que suena. En la aplicación de escritorio necesita una aplicación concreta como fuente: con System Audio (All Applications) la fila aparece en gris, porque la traducción se capturaría y se volvería a traducir. No puede cambiar mientras hay una sesión en marcha. Consulta <a href=\"/docs/features/text-only\">Con voz o solo texto</a>.",
      "screenshot": "03-translation-i-hear.png"
    },
    {
      "title": "Extensión de navegador",
      "content": "En la extensión, el Audio del otro es la pestaña en la que estás, por ejemplo tu reunión. Actívalo; el sonido propio de la pestaña se sigue reproduciendo en el <strong>Dispositivo de reproducción predeterminado</strong> elegido en el bloque Voz al iniciar la sesión."
    },
    {
      "title": "Permiso en macOS",
      "content": "En macOS, capturar todo el audio del sistema requiere el permiso <strong>Solo grabación de audio del sistema</strong>. Si no llega sonido, Sokuji te lo indica y ofrece <strong>Abrir Ajustes del Sistema</strong>. También funciona elegir una aplicación concreta."
    }
  ],
  "tips": {
    "title": "Consejos",
    "items": [
      "En Linux, usa auriculares en el modo Ambos para que tu micrófono no capte a la otra parte desde tus altavoces. En Windows y macOS, la cancelación de eco elimina de tu micrófono lo que reproduce tu ordenador.",
      "El Audio del otro se traduce como texto. Solo se te lee en voz alta cuando Traducción que yo oigo está activada."
    ]
  },
  "faq": {
    "title": "Preguntas frecuentes",
    "items": [
      {
        "question": "Las palabras de la otra parte no se traducen. ¿Qué debo comprobar?",
        "answer": "Que Sokuji esté en el modo Otro o Ambos, que el Audio del otro esté configurado con la aplicación correcta o con System Audio (All Applications), y que la llamada esté reproduciendo sonido de verdad."
      },
      {
        "question": "¿Por qué aparece en gris Traducción que yo oigo?",
        "answer": "En la aplicación de escritorio necesita una aplicación concreta como fuente del Audio del otro: elige la aplicación en lugar de System Audio (All Applications). También aparece en gris en el modo Yo, y queda fija mientras hay una sesión en marcha."
      },
      {
        "question": "¿Puedo cambiar la fuente durante una sesión?",
        "answer": "Sí, en la aplicación de escritorio. Si el cambio falla, Sokuji mantiene la fuente anterior; detén e inicia la sesión para cambiarla."
      }
    ]
  },
  "troubleshooting": {
    "title": "Solución de problemas",
    "content": "¿Sigues atascado? Envíanos tus comentarios desde el menú de la cuenta o escribe a <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. También puedes preguntar en <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const fr: TutorialData = {
  "pageTitle": "Audio de l'autre : traduire ce que dit votre interlocuteur",
  "backLink": "Retour à la documentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Aperçu",
    "content": "En modes Autre et Les deux, Sokuji a besoin de l’audio de votre interlocuteur : les voix d’une réunion, d’une vidéo ou d’un direct. L’application de bureau le prend dans une application précise ou dans tout ce que joue votre ordinateur ; l’extension de navigateur le prend dans l’onglet actuel. Le son original continue d’être lu comme d’habitude, et la traduction s’affiche sous forme de texte, ou vous est aussi lue à voix haute avec Traduction que j'entends."
  },
  "steps": [
    {
      "title": "Choisissez la source (application de bureau)",
      "content": "Dans les Paramètres, sous <strong>Audio de l'autre</strong>, choisissez l’application qui émet le son, par exemple <strong>Microsoft Teams</strong>, ou <strong>System Audio (All Applications)</strong> pour tout ce que joue votre ordinateur. Choisir l’application permet d’exclure de la traduction les sons de notification et la musique des autres programmes.",
      "screenshot": "01-others-audio.png",
      "tip": "Une application n’apparaît souvent dans la liste qu’une fois qu’elle émet du son. Rejoignez d’abord l’appel ou lancez la vidéo, puis cliquez sur l’icône d’actualisation."
    },
    {
      "title": "Changez-la depuis la fenêtre principale",
      "content": "Cliquez sur le mode sélectionné en bas à gauche. En mode Autre, il affiche <strong>Audio de l'autre</strong>, que vous pouvez activer, désactiver ou diriger vers une autre source, et <strong>Traduction que j'entends</strong>.",
      "screenshot": "02-mode-devices.png"
    },
    {
      "title": "Entendez la traduction",
      "content": "Pour que la traduction de votre interlocuteur vous soit aussi lue à voix haute, activez <strong>Traduction que j'entends</strong> dans le bloc <strong>Voix</strong> et choisissez le périphérique et le canal sur lesquels elle est lue. Dans l’application de bureau, il faut pour cela une seule application comme source : avec System Audio (All Applications), la ligne est grisée, car la traduction serait capturée et traduite de nouveau. Elle ne peut pas changer pendant une session. Voir <a href=\"/docs/features/text-only\">Voix ou texte uniquement</a>.",
      "screenshot": "03-translation-i-hear.png"
    },
    {
      "title": "Extension de navigateur",
      "content": "Dans l’extension, l’Audio de l'autre est l’onglet dans lequel vous vous trouvez, par exemple votre réunion. Activez-le ; le son de l’onglet continue d’être lu sur l’<strong>Appareil de lecture par défaut</strong> choisi dans le bloc Voix au démarrage de la session."
    },
    {
      "title": "Autorisation macOS",
      "content": "Sous macOS, capturer tout l’audio du système nécessite l’autorisation <strong>Enregistrement audio du système uniquement</strong>. Si aucun son n’arrive, Sokuji vous le signale et propose <strong>Ouvrir les Réglages Système</strong>. Choisir plutôt une application précise fonctionne aussi."
    }
  ],
  "tips": {
    "title": "Conseils",
    "items": [
      "Sous Linux, portez un casque en mode Les deux, pour que votre microphone ne capte pas votre interlocuteur depuis vos haut-parleurs. Sous Windows et macOS, l’annulation d’écho retire de votre microphone ce que joue votre ordinateur.",
      "L’Audio de l'autre est traduit sous forme de texte. Il ne vous est lu à voix haute que si Traduction que j'entends est activée."
    ]
  },
  "faq": {
    "title": "Questions fréquentes",
    "items": [
      {
        "question": "Les paroles de mon interlocuteur ne sont pas traduites. Que dois-je vérifier ?",
        "answer": "Que Sokuji est en mode Autre ou Les deux, que l’Audio de l'autre est réglé sur la bonne application ou sur System Audio (All Applications), et que l’appel émet bien du son."
      },
      {
        "question": "Pourquoi Traduction que j'entends est-elle grisée ?",
        "answer": "Dans l’application de bureau, elle nécessite une seule application comme source d’Audio de l'autre : choisissez l’application plutôt que System Audio (All Applications). Elle est aussi grisée en mode Moi, et fixée pendant une session."
      },
      {
        "question": "Puis-je changer de source pendant une session ?",
        "answer": "Oui, dans l’application de bureau. Si le changement échoue, Sokuji conserve la source précédente ; arrêtez puis relancez la session pour la changer."
      }
    ]
  },
  "troubleshooting": {
    "title": "Dépannage",
    "content": "Toujours bloqué ? Envoyez-nous un retour depuis le menu du compte, ou écrivez à <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Vous pouvez aussi poser votre question sur <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const ar: TutorialData = {
  "pageTitle": "صوت الآخر: ترجمة ما يقوله الطرف الآخر",
  "backLink": "العودة إلى التوثيق",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "نظرة عامة",
    "content": "في وضعي الآخر والكل، يحتاج Sokuji إلى صوت الطرف الآخر: أصوات اجتماع أو فيديو أو بث مباشر. يأخذه تطبيق سطح المكتب من تطبيق محدد أو من كل ما يشغّله جهازك، بينما يأخذه امتداد المتصفح من علامة التبويب الحالية. ويستمر الصوت الأصلي في التشغيل كالمعتاد، وتُعرض الترجمة كنص، أو تُقرأ لك بصوت عالٍ أيضًا مع «الترجمة التي أسمعها»."
  },
  "steps": [
    {
      "title": "اختر المصدر (تطبيق سطح المكتب)",
      "content": "في الإعدادات، تحت <strong>صوت الآخر</strong>، اختر التطبيق الذي يشغّل الصوت، مثل <strong>Microsoft Teams</strong>، أو <strong>System Audio (All Applications)</strong> لكل ما يشغّله جهازك. اختيار التطبيق يُبقي أصوات الإشعارات والموسيقى من البرامج الأخرى خارج الترجمة.",
      "screenshot": "01-others-audio.png",
      "tip": "غالبًا لا يظهر التطبيق في القائمة إلا بعد أن يشغّل صوتًا. انضم إلى المكالمة أو شغّل الفيديو أولًا، ثم انقر على أيقونة التحديث."
    },
    {
      "title": "غيّره من النافذة الرئيسية",
      "content": "انقر على الوضع المحدد أسفل اليسار. في وضع الآخر يعرض <strong>صوت الآخر</strong>، الذي يمكنك تشغيله أو إيقافه أو توجيهه إلى مصدر آخر، و<strong>الترجمة التي أسمعها</strong>.",
      "screenshot": "02-mode-devices.png"
    },
    {
      "title": "اسمع الترجمة",
      "content": "لتُقرأ لك ترجمة كلام الطرف الآخر بصوت عالٍ أيضًا، فعّل <strong>الترجمة التي أسمعها</strong> في قسم <strong>الكلام</strong> واختر الجهاز والقناة اللذين تُشغَّل عليهما. في تطبيق سطح المكتب يتطلب ذلك تطبيقًا واحدًا مصدرًا: فمع System Audio (All Applications) يكون الصف باللون الرمادي، لأن الترجمة ستُلتقط وتُترجم مرة أخرى. ولا يمكن تغييرها أثناء تشغيل الجلسة. راجع <a href=\"/docs/features/text-only\">منطوق أو نص فقط</a>.",
      "screenshot": "03-translation-i-hear.png"
    },
    {
      "title": "امتداد المتصفح",
      "content": "في الامتداد، صوت الآخر هو علامة التبويب التي أنت فيها، مثل اجتماعك. شغّله؛ يستمر صوت علامة التبويب نفسها في التشغيل على <strong>جهاز التشغيل الافتراضي</strong> المختار في قسم الكلام عند بدء الجلسة."
    },
    {
      "title": "إذن macOS",
      "content": "على macOS، يتطلب التقاط كل صوت النظام إذن <strong>تسجيل صوت النظام فقط</strong>. إذا لم يصل أي صوت، يخبرك Sokuji بذلك ويعرض <strong>فتح إعدادات النظام</strong>. ويعمل أيضًا اختيار تطبيق محدد بدلًا من ذلك."
    }
  ],
  "tips": {
    "title": "نصائح",
    "items": [
      "على Linux، ارتدِ سماعات الرأس في وضع الكل، حتى لا يلتقط ميكروفونك صوت الطرف الآخر من سماعاتك. أما على Windows و macOS فيزيل إلغاء الصدى من ميكروفونك ما يشغّله جهازك.",
      "يُترجَم صوت الآخر كنص. ولا يُقرأ لك بصوت عالٍ إلا عندما تكون «الترجمة التي أسمعها» مفعّلة."
    ]
  },
  "faq": {
    "title": "الأسئلة الشائعة",
    "items": [
      {
        "question": "كلام الطرف الآخر لا يُترجَم. ماذا أتحقق منه؟",
        "answer": "تحقّق من أن Sokuji في وضع الآخر أو الكل، وأن «صوت الآخر» مضبوط على التطبيق الصحيح أو على System Audio (All Applications)، وأن المكالمة تشغّل صوتًا فعلًا."
      },
      {
        "question": "لماذا تظهر «الترجمة التي أسمعها» باللون الرمادي؟",
        "answer": "في تطبيق سطح المكتب، تحتاج إلى تطبيق واحد مصدرًا لصوت الآخر: اختر التطبيق بدلًا من System Audio (All Applications). وتكون باللون الرمادي أيضًا في وضع أنا، ومثبّتة أثناء تشغيل الجلسة."
      },
      {
        "question": "هل يمكنني تغيير المصدر أثناء الجلسة؟",
        "answer": "نعم، في تطبيق سطح المكتب. إذا فشل التبديل، يحتفظ Sokuji بالمصدر السابق؛ أوقف الجلسة وابدأها من جديد لتغييره."
      }
    ]
  },
  "troubleshooting": {
    "title": "استكشاف الأخطاء وإصلاحها",
    "content": "هل ما زلت تواجه مشكلة؟ أرسل إلينا ملاحظاتك من قائمة الحساب، أو راسلنا على <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. يمكنك أيضًا طرح سؤالك في <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const pt: TutorialData = {
  "pageTitle": "Áudio do outro: traduza o que a outra pessoa diz",
  "backLink": "Voltar à documentação",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Visão geral",
    "content": "Nos modos Outro e Ambos, o Sokuji precisa do áudio da outra pessoa: as vozes de uma reunião, de um vídeo ou de uma transmissão ao vivo. O aplicativo de desktop o captura de um aplicativo específico ou de tudo o que o seu computador reproduz; a extensão de navegador o captura da aba atual. O som original continua tocando normalmente, e a tradução é mostrada como texto ou, com Tradução que eu ouço, também lida em voz alta para você."
  },
  "steps": [
    {
      "title": "Escolha a fonte (aplicativo de desktop)",
      "content": "Em Configurações, em <strong>Áudio do outro</strong>, escolha o aplicativo que reproduz o som, por exemplo o <strong>Microsoft Teams</strong>, ou <strong>System Audio (All Applications)</strong> para tudo o que o seu computador reproduz. Escolher o aplicativo mantém sons de notificação e músicas de outros programas fora da tradução.",
      "screenshot": "01-others-audio.png",
      "tip": "Muitas vezes um aplicativo só aparece na lista quando está reproduzindo som. Entre primeiro na chamada ou inicie o vídeo e depois clique no ícone de atualizar."
    },
    {
      "title": "Altere pela janela principal",
      "content": "Clique no modo selecionado, no canto inferior esquerdo. No modo Outro, ele mostra o <strong>Áudio do outro</strong>, que você pode ativar, desativar ou apontar para outra fonte, e <strong>Tradução que eu ouço</strong>.",
      "screenshot": "02-mode-devices.png"
    },
    {
      "title": "Ouça a tradução",
      "content": "Para que a tradução da outra pessoa também seja lida em voz alta para você, ative <strong>Tradução que eu ouço</strong> no bloco <strong>Voz</strong> e escolha o dispositivo e o canal em que ela toca. No aplicativo de desktop, isso exige um aplicativo específico como fonte: com System Audio (All Applications), a linha fica esmaecida, porque a tradução seria capturada e traduzida de novo. Ela não pode ser alterada durante uma sessão. Veja <a href=\"/docs/features/text-only\">Com voz ou somente texto</a>.",
      "screenshot": "03-translation-i-hear.png"
    },
    {
      "title": "Extensão de navegador",
      "content": "Na extensão, o Áudio do outro é a aba em que você está, por exemplo a sua reunião. Ative-o; o som da própria aba continua tocando no <strong>Dispositivo de reprodução padrão</strong> escolhido no bloco Voz quando a sessão começa."
    },
    {
      "title": "Permissão no macOS",
      "content": "No macOS, capturar todo o áudio do sistema exige a permissão <strong>Apenas Gravação de Áudio do Sistema</strong>. Se nenhum som chegar, o Sokuji avisa e oferece <strong>Abrir Ajustes do Sistema</strong>. Escolher um aplicativo específico também funciona."
    }
  ],
  "tips": {
    "title": "Dicas",
    "items": [
      "No Linux, use fones de ouvido no modo Ambos, para que o seu microfone não capte a outra pessoa pelos alto-falantes. No Windows e no macOS, o cancelamento de eco remove do seu microfone o que o computador reproduz.",
      "O Áudio do outro é traduzido como texto. Ele só é lido em voz alta para você quando Tradução que eu ouço está ativada."
    ]
  },
  "faq": {
    "title": "Perguntas frequentes",
    "items": [
      {
        "question": "As palavras da outra pessoa não são traduzidas. O que devo verificar?",
        "answer": "Se o Sokuji está no modo Outro ou Ambos, se o Áudio do outro está definido como o aplicativo certo ou como System Audio (All Applications) e se a chamada está de fato reproduzindo som."
      },
      {
        "question": "Por que Tradução que eu ouço aparece esmaecida?",
        "answer": "No aplicativo de desktop, ela precisa de um aplicativo específico como fonte do Áudio do outro: escolha o aplicativo em vez de System Audio (All Applications). Ela também fica esmaecida no modo Eu e fixa durante uma sessão."
      },
      {
        "question": "Posso trocar a fonte durante uma sessão?",
        "answer": "Sim, no aplicativo de desktop. Se a troca falhar, o Sokuji mantém a fonte anterior; encerre e inicie a sessão para trocá-la."
      }
    ]
  },
  "troubleshooting": {
    "title": "Solução de problemas",
    "content": "Ainda com dificuldades? Envie seu feedback pelo menu da conta ou escreva para <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Você também pode perguntar no <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const ru: TutorialData = {
  "pageTitle": "Аудио собеседника: перевод того, что говорит другая сторона",
  "backLink": "Назад к документации",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Обзор",
    "content": "В режимах «Собеседник» и «Оба» Sokuji нужен звук другой стороны: голоса на встрече, в видео или прямой трансляции. Настольное приложение берёт его из определённого приложения или из всего, что воспроизводит компьютер; расширение для браузера — из текущей вкладки. Исходный звук продолжает воспроизводиться как обычно, а перевод показывается в виде текста или, если включён «Перевод, который слышу я», ещё и зачитывается вам вслух."
  },
  "steps": [
    {
      "title": "Выберите источник (настольное приложение)",
      "content": "В настройках, в разделе <strong>Аудио собеседника</strong>, выберите приложение, которое воспроизводит звук, например <strong>Microsoft Teams</strong>, или <strong>System Audio (All Applications)</strong> для всего, что воспроизводит компьютер. Если выбрать приложение, звуки уведомлений и музыка из других программ не попадут в перевод.",
      "screenshot": "01-others-audio.png",
      "tip": "Приложение часто появляется в списке только после того, как начнёт воспроизводить звук. Сначала подключитесь к звонку или запустите видео, затем нажмите значок обновления."
    },
    {
      "title": "Меняйте его из главного окна",
      "content": "Нажмите выбранный режим в левом нижнем углу. В режиме «Собеседник» там показаны <strong>Аудио собеседника</strong>, которое можно включить или выключить или выбрать для него другой источник, и <strong>Перевод, который слышу я</strong>.",
      "screenshot": "02-mode-devices.png"
    },
    {
      "title": "Слушайте перевод",
      "content": "Чтобы перевод собеседника ещё и зачитывался вам вслух, включите <strong>Перевод, который слышу я</strong> в блоке <strong>Речь</strong> и выберите устройство и канал, на которых он звучит. В настольном приложении для этого источником должно быть одно приложение: с System Audio (All Applications) строка серая, потому что перевод был бы захвачен и переведён снова. Во время сеанса этот параметр изменить нельзя. См. <a href=\"/docs/features/text-only\">Озвучивание или только текст</a>.",
      "screenshot": "03-translation-i-hear.png"
    },
    {
      "title": "Расширение для браузера",
      "content": "В расширении аудио собеседника — это вкладка, в которой вы находитесь, например ваша встреча. Включите его; собственный звук вкладки по-прежнему воспроизводится через <strong>Устройство воспроизведения по умолчанию</strong>, выбранное в блоке «Речь» при запуске сеанса."
    },
    {
      "title": "Разрешение в macOS",
      "content": "В macOS для захвата всего системного звука нужно разрешение <strong>Только запись системного звука</strong>. Если звук не поступает, Sokuji сообщит об этом и предложит <strong>Открыть Системные настройки</strong>. Можно также выбрать конкретное приложение."
    }
  ],
  "tips": {
    "title": "Советы",
    "items": [
      "В Linux в режиме «Оба» надевайте наушники, чтобы микрофон не улавливал собеседника из динамиков. В Windows и macOS эхоподавление удаляет из микрофона то, что воспроизводит компьютер.",
      "Аудио собеседника переводится в виде текста. Перевод зачитывается вам вслух, только когда включён «Перевод, который слышу я»."
    ]
  },
  "faq": {
    "title": "Часто задаваемые вопросы",
    "items": [
      {
        "question": "Слова собеседника не переводятся. Что проверить?",
        "answer": "Что Sokuji в режиме «Собеседник» или «Оба», что в аудио собеседника выбрано нужное приложение или System Audio (All Applications) и что звонок действительно воспроизводит звук."
      },
      {
        "question": "Почему «Перевод, который слышу я» серый?",
        "answer": "В настольном приложении ему нужно одно приложение в качестве источника аудио собеседника: выберите приложение вместо System Audio (All Applications). Он также серый в режиме «Я» и зафиксирован, пока идёт сеанс."
      },
      {
        "question": "Можно ли сменить источник во время сеанса?",
        "answer": "Да, в настольном приложении. Если переключиться не удалось, Sokuji оставляет прежний источник; чтобы сменить его, остановите и снова запустите сеанс."
      }
    ]
  },
  "troubleshooting": {
    "title": "Устранение неполадок",
    "content": "Всё ещё не получается? Отправьте нам отзыв через меню учётной записи или напишите на <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Также можно задать вопрос в <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const de: TutorialData = {
  "pageTitle": "Audio des Gegenübers: übersetzen, was die andere Seite sagt",
  "backLink": "Zurück zur Dokumentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Übersicht",
    "content": "In den Modi „Gegenüber“ und „Beides“ braucht Sokuji den Ton der anderen Seite: die Stimmen eines Meetings, eines Videos oder eines Livestreams. Die Desktop-App nimmt ihn von einer bestimmten Anwendung oder von allem, was Ihr Computer wiedergibt; die Browsererweiterung nimmt ihn vom aktuellen Tab. Der Originalton wird weiter wie gewohnt wiedergegeben, und die Übersetzung wird als Text angezeigt oder mit „Übersetzung, die ich höre“ auch vorgelesen."
  },
  "steps": [
    {
      "title": "Die Quelle wählen (Desktop-App)",
      "content": "Wählen Sie in den Einstellungen unter <strong>Audio des Gegenübers</strong> die Anwendung, die den Ton wiedergibt, zum Beispiel <strong>Microsoft Teams</strong>, oder <strong>System Audio (All Applications)</strong> für alles, was Ihr Computer wiedergibt. Wenn Sie die Anwendung wählen, gelangen Benachrichtigungstöne und Musik aus anderen Programmen nicht in die Übersetzung.",
      "screenshot": "01-others-audio.png",
      "tip": "Eine Anwendung erscheint oft erst in der Liste, wenn sie Ton wiedergibt. Treten Sie zuerst dem Anruf bei oder starten Sie das Video, und klicken Sie dann auf das Aktualisieren-Symbol."
    },
    {
      "title": "Im Hauptfenster ändern",
      "content": "Klicken Sie unten links auf den ausgewählten Modus. Im Modus „Gegenüber“ zeigt er <strong>Audio des Gegenübers</strong>, das Sie ein- oder ausschalten oder auf eine andere Quelle richten können, und <strong>Übersetzung, die ich höre</strong>.",
      "screenshot": "02-mode-devices.png"
    },
    {
      "title": "Die Übersetzung hören",
      "content": "Damit Ihnen die Übersetzung Ihres Gegenübers auch vorgelesen wird, schalten Sie im Bereich <strong>Sprache</strong> <strong>Übersetzung, die ich höre</strong> ein und wählen Sie das Gerät und den Kanal für die Wiedergabe. In der Desktop-App braucht das eine einzelne Anwendung als Quelle: Mit System Audio (All Applications) ist die Zeile ausgegraut, weil die Übersetzung sonst aufgenommen und erneut übersetzt würde. Während eine Sitzung läuft, lässt sie sich nicht ändern. Siehe <a href=\"/docs/features/text-only\">Gesprochen oder nur Text</a>.",
      "screenshot": "03-translation-i-hear.png"
    },
    {
      "title": "Browsererweiterung",
      "content": "In der Erweiterung ist das Audio des Gegenübers der Tab, in dem Sie sich befinden, zum Beispiel Ihr Meeting. Schalten Sie es ein; der Ton des Tabs selbst läuft weiter über das <strong>Standard-Wiedergabegerät</strong>, das beim Start der Sitzung im Bereich „Sprache“ gewählt ist."
    },
    {
      "title": "macOS-Berechtigung",
      "content": "Unter macOS braucht die Aufnahme des gesamten Systemtons die Berechtigung <strong>Nur Systemaudioaufnahme</strong>. Kommt kein Ton an, weist Sokuji darauf hin und bietet <strong>Systemeinstellungen öffnen</strong> an. Stattdessen eine bestimmte Anwendung zu wählen, funktioniert ebenfalls."
    }
  ],
  "tips": {
    "title": "Tipps",
    "items": [
      "Tragen Sie unter Linux im Modus „Beides“ Kopfhörer, damit Ihr Mikrofon Ihr Gegenüber nicht aus den Lautsprechern aufnimmt. Unter Windows und macOS entfernt die Echounterdrückung, was Ihr Computer wiedergibt, aus Ihrem Mikrofonsignal.",
      "Das Audio des Gegenübers wird als Text übersetzt. Vorgelesen wird es Ihnen nur, wenn „Übersetzung, die ich höre“ eingeschaltet ist."
    ]
  },
  "faq": {
    "title": "Häufig gestellte Fragen",
    "items": [
      {
        "question": "Was die andere Seite sagt, wird nicht übersetzt. Was sollte ich prüfen?",
        "answer": "Dass Sokuji im Modus „Gegenüber“ oder „Beides“ ist, dass das Audio des Gegenübers auf die richtige Anwendung oder auf System Audio (All Applications) eingestellt ist und dass der Anruf tatsächlich Ton wiedergibt."
      },
      {
        "question": "Warum ist „Übersetzung, die ich höre“ ausgegraut?",
        "answer": "In der Desktop-App braucht sie eine einzelne Anwendung als Quelle für das Audio des Gegenübers: Wählen Sie die Anwendung statt System Audio (All Applications). Im Modus „Ich“ ist sie ebenfalls ausgegraut, und während einer Sitzung ist sie festgelegt."
      },
      {
        "question": "Kann ich die Quelle während einer Sitzung ändern?",
        "answer": "Ja, in der Desktop-App. Schlägt der Wechsel fehl, behält Sokuji die vorherige Quelle; beenden und starten Sie die Sitzung, um sie zu ändern."
      }
    ]
  },
  "troubleshooting": {
    "title": "Fehlerbehebung",
    "content": "Kommen Sie nicht weiter? Senden Sie uns Feedback über das Kontomenü oder schreiben Sie an <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Sie können auch in den <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> fragen."
  }
};

const uk: TutorialData = {
  "pageTitle": "Аудіо співрозмовника: переклад того, що каже інша сторона",
  "backLink": "Назад до документації",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Огляд",
    "content": "У режимах «Співрозмовник» і «Обидва» Sokuji потрібен звук іншої сторони: голоси на зустрічі, у відео чи прямій трансляції. Настільний застосунок бере його з певного застосунку або з усього, що відтворює комп’ютер; розширення для браузера — з поточної вкладки. Оригінальний звук і далі відтворюється як зазвичай, а переклад показується у вигляді тексту або, якщо ввімкнено «Переклад, який чую я», ще й зачитується вам уголос."
  },
  "steps": [
    {
      "title": "Виберіть джерело (настільний застосунок)",
      "content": "У налаштуваннях, у розділі <strong>Аудіо співрозмовника</strong>, виберіть застосунок, що відтворює звук, наприклад <strong>Microsoft Teams</strong>, або <strong>System Audio (All Applications)</strong> для всього, що відтворює комп’ютер. Якщо вибрати застосунок, звуки сповіщень і музика з інших програм не потраплять у переклад.",
      "screenshot": "01-others-audio.png",
      "tip": "Застосунок часто з’являється в списку лише тоді, коли починає відтворювати звук. Спочатку приєднайтеся до дзвінка або запустіть відео, потім натисніть значок оновлення."
    },
    {
      "title": "Змінюйте його з головного вікна",
      "content": "Натисніть вибраний режим у лівому нижньому куті. У режимі «Співрозмовник» там показано <strong>Аудіо співрозмовника</strong>, яке можна ввімкнути чи вимкнути або вибрати для нього інше джерело, і <strong>Переклад, який чую я</strong>.",
      "screenshot": "02-mode-devices.png"
    },
    {
      "title": "Слухайте переклад",
      "content": "Щоб переклад співрозмовника ще й зачитувався вам уголос, увімкніть <strong>Переклад, який чую я</strong> в блоці <strong>Мовлення</strong> й виберіть пристрій і канал, на яких він звучить. У настільному застосунку для цього джерелом має бути один застосунок: із System Audio (All Applications) рядок сірий, бо переклад було б захоплено й перекладено знову. Під час сеансу цей параметр змінити не можна. Див. <a href=\"/docs/features/text-only\">Озвучування або лише текст</a>.",
      "screenshot": "03-translation-i-hear.png"
    },
    {
      "title": "Розширення для браузера",
      "content": "У розширенні аудіо співрозмовника — це вкладка, у якій ви перебуваєте, наприклад ваша зустріч. Увімкніть його; власний звук вкладки й далі відтворюється через <strong>Пристрій відтворення за замовчуванням</strong>, вибраний у блоці «Мовлення» під час запуску сеансу."
    },
    {
      "title": "Дозвіл у macOS",
      "content": "У macOS для захоплення всього системного звуку потрібен дозвіл <strong>Лише запис системного звуку</strong>. Якщо звук не надходить, Sokuji повідомить про це й запропонує <strong>Відкрити Системні параметри</strong>. Можна також вибрати конкретний застосунок."
    }
  ],
  "tips": {
    "title": "Поради",
    "items": [
      "У Linux у режимі «Обидва» надягайте навушники, щоб мікрофон не вловлював співрозмовника з динаміків. У Windows і macOS придушення відлуння прибирає з мікрофона те, що відтворює комп’ютер.",
      "Аудіо співрозмовника перекладається у вигляді тексту. Переклад зачитується вам уголос, лише коли ввімкнено «Переклад, який чую я»."
    ]
  },
  "faq": {
    "title": "Часті запитання",
    "items": [
      {
        "question": "Слова співрозмовника не перекладаються. Що перевірити?",
        "answer": "Що Sokuji в режимі «Співрозмовник» або «Обидва», що в аудіо співрозмовника вибрано потрібний застосунок або System Audio (All Applications) і що дзвінок справді відтворює звук."
      },
      {
        "question": "Чому «Переклад, який чую я» сірий?",
        "answer": "У настільному застосунку йому потрібен один застосунок як джерело аудіо співрозмовника: виберіть застосунок замість System Audio (All Applications). Він також сірий у режимі «Я» і зафіксований, поки триває сеанс."
      },
      {
        "question": "Чи можна змінити джерело під час сеансу?",
        "answer": "Так, у настільному застосунку. Якщо перемкнутися не вдалося, Sokuji залишає попереднє джерело; щоб змінити його, зупиніть і знову запустіть сеанс."
      }
    ]
  },
  "troubleshooting": {
    "title": "Усунення несправностей",
    "content": "Досі не виходить? Надішліть нам відгук через меню облікового запису або напишіть на <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Також можна запитати в <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const it: TutorialData = {
  "pageTitle": "Audio dell'altro: traduci ciò che dice l’altra parte",
  "backLink": "Torna alla documentazione",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Panoramica",
    "content": "Nelle modalità Altro ed Entrambi, Sokuji ha bisogno dell’audio dell’altra parte: le voci di una riunione, di un video o di una diretta. L’app desktop lo prende da un’applicazione specifica o da tutto ciò che riproduce il computer; l’estensione per browser lo prende dalla scheda corrente. L’audio originale continua a essere riprodotto come sempre, e la traduzione viene mostrata come testo, oppure anche letta ad alta voce per te con Traduzione che sento io."
  },
  "steps": [
    {
      "title": "Scegli la sorgente (app desktop)",
      "content": "In Impostazioni, sotto <strong>Audio dell'altro</strong>, scegli l’applicazione che riproduce l’audio, per esempio <strong>Microsoft Teams</strong>, oppure <strong>System Audio (All Applications)</strong> per tutto ciò che riproduce il computer. Scegliendo l’applicazione, i suoni di notifica e la musica di altri programmi restano fuori dalla traduzione.",
      "screenshot": "01-others-audio.png",
      "tip": "Spesso un’applicazione compare nell’elenco solo quando riproduce audio. Entra prima nella chiamata o avvia il video, poi fai clic sull’icona di aggiornamento."
    },
    {
      "title": "Cambiala dalla finestra principale",
      "content": "Fai clic sulla modalità selezionata in basso a sinistra. In modalità Altro mostra <strong>Audio dell'altro</strong>, che puoi attivare, disattivare o indirizzare verso un’altra sorgente, e <strong>Traduzione che sento io</strong>.",
      "screenshot": "02-mode-devices.png"
    },
    {
      "title": "Ascolta la traduzione",
      "content": "Per farti leggere ad alta voce anche la traduzione dell’altra parte, attiva <strong>Traduzione che sento io</strong> nel blocco <strong>Voce</strong> e scegli il dispositivo e il canale su cui viene riprodotta. Nell’app desktop serve una sola applicazione come sorgente: con System Audio (All Applications) la riga è in grigio, perché la traduzione verrebbe catturata e tradotta di nuovo. Non può cambiare mentre una sessione è attiva. Vedi <a href=\"/docs/features/text-only\">Voce o solo testo</a>.",
      "screenshot": "03-translation-i-hear.png"
    },
    {
      "title": "Estensione per browser",
      "content": "Nell’estensione, l’Audio dell'altro è la scheda in cui ti trovi, per esempio la tua riunione. Attivalo; l’audio della scheda continua a essere riprodotto sul <strong>Dispositivo di riproduzione predefinito</strong> scelto nel blocco Voce all’avvio della sessione."
    },
    {
      "title": "Autorizzazione su macOS",
      "content": "Su macOS, acquisire tutto l’audio di sistema richiede l’autorizzazione <strong>Solo registrazione audio di sistema</strong>. Se non arriva alcun audio, Sokuji te lo segnala e propone <strong>Apri Impostazioni di Sistema</strong>. Funziona anche scegliere un’applicazione specifica."
    }
  ],
  "tips": {
    "title": "Consigli",
    "items": [
      "Su Linux, indossa le cuffie in modalità Entrambi, così il microfono non capta l’altra parte dai tuoi altoparlanti. Su Windows e macOS, la cancellazione dell’eco rimuove dal microfono ciò che riproduce il computer.",
      "L’Audio dell'altro viene tradotto come testo. Viene letto ad alta voce per te solo quando Traduzione che sento io è attiva."
    ]
  },
  "faq": {
    "title": "Domande frequenti",
    "items": [
      {
        "question": "Le parole dell’altra parte non vengono tradotte. Cosa devo controllare?",
        "answer": "Che Sokuji sia in modalità Altro o Entrambi, che l’Audio dell'altro sia impostato sull’applicazione giusta o su System Audio (All Applications), e che la chiamata stia effettivamente riproducendo audio."
      },
      {
        "question": "Perché Traduzione che sento io è in grigio?",
        "answer": "Nell’app desktop richiede una sola applicazione come sorgente dell’Audio dell'altro: scegli l’applicazione invece di System Audio (All Applications). È in grigio anche in modalità Io, e fissa mentre una sessione è attiva."
      },
      {
        "question": "Posso cambiare sorgente durante una sessione?",
        "answer": "Sì, nell’app desktop. Se il cambio non riesce, Sokuji mantiene la sorgente precedente; interrompi e riavvia la sessione per cambiarla."
      }
    ]
  },
  "troubleshooting": {
    "title": "Risoluzione dei problemi",
    "content": "Ancora bloccato? Inviaci un feedback dal menu dell’account, oppure scrivi a <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Puoi anche chiedere su <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const translations: Record<Locale, TutorialData> = { en, zh, ja, ko, es, fr, ar, pt, ru, de, uk, it };

export function ParticipantAudio() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/features/participant-audio" />;
}
